import { useEffect, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Flag, LifeBuoy, Gavel, BadgeCheck, Scale, ArrowRight } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchModerationOverview, type ModerationOverview } from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'

/**
 * A2.03 — Moderatsiya dashboard'i. Bugun qurilgan barcha trust&safety
 * navbatlarining (shikoyat/tiket/apellyatsiya/verifikatsiya/huquqiy) KUTAYOTGAN
 * yuklamasini bitta ops-ko'rinishда jamlaydi. Jonli endpointlar ustidan
 * (har biri `?status=pending`) — yangi backend/agregatsiya KUTMAYDI. Har karta
 * bosilса o'z navbatiga o'tadi. Xodimning «uy» ekrani: nima kutmoqda va qayerda.
 */
export function ModerationDashboard() {
  const t = useT()
  const [data, setData] = useState<ModerationOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchModerationOverview(ac.signal)
      .then((d) => {
        if (alive) setData(d)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  const total = data
    ? data.reports_pending +
      data.tickets_open +
      data.appeals_pending +
      data.verifications_pending +
      data.legal_pending
    : 0

  const queues: {
    to: string
    label: string
    count: number
    icon: ReactNode
  }[] = data
    ? [
        { to: '/moderation', label: t.modDash.reports, count: data.reports_pending, icon: <Flag size={16} /> },
        { to: '/support', label: t.modDash.tickets, count: data.tickets_open, icon: <LifeBuoy size={16} /> },
        { to: '/appeals', label: t.modDash.appeals, count: data.appeals_pending, icon: <Gavel size={16} /> },
        {
          to: '/verification',
          label: t.modDash.verifications,
          count: data.verifications_pending,
          icon: <BadgeCheck size={16} />,
        },
        { to: '/legal', label: t.modDash.legal, count: data.legal_pending, icon: <Scale size={16} /> },
      ]
    : []

  return (
    <>
      <PageHeader title={t.modDash.title} screenId="A2.03" description={t.modDash.note} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {/* Umumiy ochiq yuklama */}
        <div className="rounded-lg border border-border-default bg-surface-2 p-4">
          <div className="text-xs text-tertiary">{t.modDash.openWorkload}</div>
          {loading ? (
            <Skeleton className="mt-1.5 h-9 w-24" />
          ) : (
            <div
              className={`mt-0.5 font-mono text-2xl tabular ${total > 0 ? 'text-warning' : 'text-success'}`}
            >
              {total.toLocaleString()}
            </div>
          )}
        </div>

        {/* Navbatlar — bosiladigan kartalar */}
        <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {loading
            ? Array.from({ length: 5 }, (_, i) => (
                <div key={i} className="rounded-lg border border-border-default bg-surface-2 p-3">
                  <Skeleton className="h-5 w-full" />
                  <Skeleton className="mt-2 h-7 w-12" />
                </div>
              ))
            : queues.map((q) => (
                <Link
                  key={q.to}
                  to={q.to}
                  className="group flex items-center justify-between rounded-lg border border-border-default bg-surface-2 p-3 transition-colors hover:border-border-strong hover:bg-surface-3"
                >
                  <div>
                    <div className="flex items-center gap-1.5 text-sm text-secondary">
                      <span className="text-tertiary">{q.icon}</span>
                      {q.label}
                    </div>
                    <div
                      className={`mt-0.5 font-mono text-2xl tabular ${
                        q.count > 0 ? 'text-warning' : 'text-tertiary'
                      }`}
                    >
                      {q.count.toLocaleString()}
                    </div>
                  </div>
                  <ArrowRight
                    size={16}
                    className="text-tertiary opacity-0 transition-opacity group-hover:opacity-100"
                  />
                </Link>
              ))}
        </div>
      </div>
    </>
  )
}
